import type { CSSProperties } from 'react';

import type { JSONContent } from '@tiptap/core';
import classnames from 'classnames';

import RedirectLink from './components/RedirectLink';
import Serializer from './components/Serializer';

import { computeFont } from '../utils/fonts';
import { defaultStructure as defaultTagsStructure } from './utils/defaultStructure';
import tagSlug from './utils/tagSlug';

import type { SerializableNode } from '../serializer';
import { TagsSerializer } from './Serializer';
import * as styles from './Tags.css';
import type { TagsElementProps } from './Tags.types';

const COLUMNS_MAP: Record<string, string> = {
  '1': 'grid-cols-1',
  '2': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2',
  '3': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  '4': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  '5': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5',
  '6': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-6',
};

export function TagsElement(props: TagsElementProps) {
  const { element, children, attributes, className, isLive } = props;
  const {
    width,
    maxWidth,
    customAttributes,
    borderWidth,
    borderRadius,
    borderColor,
    borderStyle,
    padding,
    gap,
    mobileGap,
    shadow,
    columns,
    backgroundColor,
    data = [],
    nameColor,
    nameFontFamily,
    nameFontSize,
    nameFontWeight,
    descriptionFontFamily,
    descriptionFontWeight,
    alignment,
    mobileDirection = 'direction_vertical',
    cardStructureV2,
    structureTiptapState,
  } = element.attrs || {};

  const tagsCount = data?.length || 0;

  if (cardStructureV2) {
    return (
      <div
        {...customAttributes}
        className={classnames(
          className,
          styles.tagsElement,
          customAttributes?.className,
          'relative',
          COLUMNS_MAP[columns || '3'],
        )}
        style={
          {
            width,
            maxWidth,
            '--tags-display': 'grid',
            '--tags-gap': gap,
            '--tags-mobile-gap': mobileGap,
          } as CSSProperties
        }
        {...attributes}
      >
        {data?.map((tag) => {
          if (isLive) {
            return (
              <RedirectLink
                key={tag?.id}
                to={`/t/${tagSlug(tag?.slug, tag?.display)}`}
                style={
                  {
                    flexBasis: '80%',
                    flexShrink: 0,
                  } as CSSProperties
                }
              >
                <TagsSerializer
                  node={
                    (structureTiptapState?.content?.[0] || defaultTagsStructure.content[0]) as JSONContent &
                      SerializableNode
                  }
                  rootClassName={'h-full'}
                  rootStyle={{}}
                  tag={tag}
                />
              </RedirectLink>
            );
          }

          return (
            <div
              key={tag.id}
              style={
                {
                  flexBasis: '80%',
                  flexShrink: 0,
                } as CSSProperties
              }
            >
              <TagsSerializer
                node={
                  (structureTiptapState?.content?.[0] || defaultTagsStructure.content[0]) as JSONContent &
                    SerializableNode
                }
                rootClassName={'h-full'}
                rootStyle={{}}
                tag={tag}
              />
            </div>
          );
        })}
        {children}
      </div>
    );
  }

  return (
    <div
      {...customAttributes}
      className={classnames(
        className,
        styles.tagsElement,
        customAttributes?.className,
        'relative',
        COLUMNS_MAP[columns || '3'],
      )}
      style={
        {
          gap: gap,
          width,
          maxWidth,
          '--padding': padding,
          '--background-color': backgroundColor,
          '--tags-name-color': nameColor,
          '--tags-name-font-family': computeFont(nameFontFamily) || 'inherit',
          '--tags-name-font-size': nameFontSize,
          '--tags-name-font-weight': nameFontWeight || 600,
          '--tags-description-font-family': computeFont(descriptionFontFamily) || 'inherit',
          '--alignment': alignment,
          '--tags-description-font-weight': descriptionFontWeight || 400,
          '--tags-display': 'grid',
          '--tags-mobile-display': mobileDirection === 'direction_horizontal' ? 'flex' : 'grid',
          '--tags-mobile-x-overflow': mobileDirection === 'direction_horizontal' ? 'scroll' : 'visible',
        } as CSSProperties
      }
      {...attributes}
    >
      {tagsCount > 0
        ? data?.map((tag) => {
            if (isLive) {
              return (
                <RedirectLink
                  key={tag?.id}
                  to={`/t/${tagSlug(tag?.slug, tag?.display)}`}
                  style={
                    {
                      textDecoration: 'none',
                      borderWidth: borderWidth,
                      borderStyle: borderStyle,
                      borderColor: borderColor,
                      borderRadius: borderRadius,
                      boxShadow: shadow,
                      backgroundColor: backgroundColor,
                      padding: padding,
                      textAlign: alignment,
                      flexBasis: '80%',
                      flexShrink: 0,
                    } as CSSProperties
                  }
                >
                  <Serializer tag={tag} attributes={element.attrs} />
                </RedirectLink>
              );
            }

            return (
              <div
                key={tag?.id}
                style={
                  {
                    textDecoration: 'none',
                    borderWidth: borderWidth,
                    borderStyle: borderStyle,
                    borderColor: borderColor,
                    borderRadius: borderRadius,
                    boxShadow: shadow,
                    backgroundColor: backgroundColor,
                    padding: padding,
                    textAlign: alignment,
                    flexBasis: '80%',
                    flexShrink: 0,
                  } as CSSProperties
                }
              >
                <Serializer tag={tag} attributes={element.attrs} />
              </div>
            );
          })
        : null}
      {children}
    </div>
  );
}
